import { useRef, useState } from "react";
import { toast } from "sonner";
import { useAddEvent } from "../../../../hooks/track/useAddEvent";
import { useUpdateEvent } from "../../../../hooks/track/useUpdateEvent";
import { useAnalytics } from "../../../../lib/analytics";
import { useClientTranslations } from "../../../../lib/i18n/client";
import {
	TRACK_EVENT_CREATED,
	TRACK_EVENT_EDITED,
} from "../../../../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../../../../lib/track/parseSyntheticEventId";
import {
	type AdminFormState,
	validateAdminForm,
} from "../../../../services/formValidationService";
import type { CreateEventInput, Event } from "../../../../types/event";
import {
	ContactPicker,
	DatePicker,
	FormField,
	Select,
	type SelectOption,
	TimePicker,
} from "../fields";
import { RecurringSeriesBanner } from "../RecurringSeriesBanner";
import styles from "./AdminForm.module.css";
import { EventFormShell } from "./EventFormShell";

type FormMode = "create" | "edit";

interface AdminFormProps {
	mode: FormMode;
	initialValues?: Event;
	onClose: () => void;
}

// Curated admin task subtype keys (UI-SPEC §"Curated dropdown values").
const ADMIN_TASK_KEYS = [
	"insurancePreAuthorization",
	"pharmacyRefill",
	"medicationDelivery",
	"recordsRequest",
	"geneticResults",
	"scheduleNextCycle",
	"financialCounseling",
	"other",
] as const;

function formatDateTime(date: Date, time: string): string {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}T${time}:00`;
}

function getDeviceTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return "America/New_York";
	}
}

/**
 * Phase 37 — Admin task form (ADD-06 + EDM-04).
 *
 * Field order (UI-SPEC §"Body — form (per type) → AdminForm"):
 *   Task type → Date → Time → Contact (REQUIRED) → Notes.
 *
 * dateTime composition: `${YYYY-MM-DD}T${HH:mm}:00` (wall-clock-naive).
 * Title (Q4): subtypeLabel only.
 * `isRecurring` analytics value: always false (admin tasks are one-off).
 *
 * Save lifecycle mirrors AppointmentTestForm (Plan 07):
 *   - Idempotency-Key via UUIDv4 in useRef (Pattern 2 — reset on success/error)
 *   - Edit mode: parseSyntheticEventId → baseId before PUT (Pitfall 2)
 *   - Analytics enums-only: { eventType: 'admin', isRecurring: false }
 *   - Toast static i18n keys (no PHI interpolation)
 */
export function AdminForm({ mode, initialValues, onClose }: AdminFormProps) {
	const { t } = useClientTranslations("track");
	const { logEvent } = useAnalytics();

	const [state, setState] = useState<AdminFormState>(() => {
		if (initialValues) {
			const dt = new Date(initialValues.dateTime);
			const hh = String(dt.getHours()).padStart(2, "0");
			const mm = String(dt.getMinutes()).padStart(2, "0");
			return {
				subtype: initialValues.subtype ?? null,
				date: dt,
				time: `${hh}:${mm}`,
				contactId: initialValues.contactId ?? null,
				notes: initialValues.notes ?? "",
			};
		}
		return {
			subtype: null,
			date: null,
			time: null,
			contactId: null,
			notes: "",
		};
	});

	const idempotencyKeyRef = useRef<string | null>(null);
	const addMutation = useAddEvent();
	const updateMutation = useUpdateEvent();
	const isPending = addMutation.isPending || updateMutation.isPending;
	const validation = validateAdminForm(state);

	// Phase 37 D-19 — render whole-series banner above body in edit mode when synthetic id detected.
	const isRecurringInstance =
		mode === "edit" && initialValues
			? parseSyntheticEventId(initialValues.id) !== null
			: false;

	const handleSave = () => {
		if (!validation.isValid) return;
		if (!state.date || !state.time) return;

		const subtypeLabel = state.subtype
			? t(`event.add.subtypes.adminType.${state.subtype}`)
			: "";
		const dateTime = formatDateTime(state.date, state.time);

		const body: CreateEventInput = {
			type: "admin",
			subtype: state.subtype,
			title: subtypeLabel,
			notes: state.notes.trim() || null,
			dateTime,
			timezone: initialValues?.timezone ?? getDeviceTimezone(),
			contactId: state.contactId,
		};

		if (mode === "create") {
			if (!idempotencyKeyRef.current) {
				idempotencyKeyRef.current = crypto.randomUUID();
			}
			addMutation.mutate(
				{ body, idempotencyKey: idempotencyKeyRef.current },
				{
					onSuccess: () => {
						idempotencyKeyRef.current = null;
						logEvent(TRACK_EVENT_CREATED, undefined, {
							eventType: "admin",
							isRecurring: false,
						});
						toast.success(t("event.add.successToast"));
						onClose();
					},
					onError: () => {
						idempotencyKeyRef.current = null;
						toast.error(t("event.add.errors.save"));
					},
				},
			);
			return;
		}

		if (initialValues) {
			const baseId =
				parseSyntheticEventId(initialValues.id)?.baseId ?? initialValues.id;
			updateMutation.mutate(
				{ id: baseId, patch: body },
				{
					onSuccess: () => {
						logEvent(TRACK_EVENT_EDITED, undefined, {
							eventType: "admin",
							isRecurring: !!initialValues.frequencyRRULE,
						});
						toast.success(t("event.edit.successToast"));
						onClose();
					},
					onError: () => {
						toast.error(t("event.edit.errorToast"));
					},
				},
			);
		}
	};

	const subtypeId = "admin-subtype";
	const dateId = "admin-date";
	const timeId = "admin-time";
	const contactId = "admin-contact";
	const notesId = "admin-notes";

	return (
		<EventFormShell
			mode={mode}
			isValid={validation.isValid}
			isPending={isPending}
			onSave={handleSave}
			recurringBanner={
				isRecurringInstance && initialValues ? (
					<RecurringSeriesBanner type={initialValues.type} variant="edit" />
				) : undefined
			}
		>
			<FormField
				id={subtypeId}
				label={t("event.add.fields.subtype.admin.label")}
				required
			>
				<Select
					id={subtypeId}
					value={state.subtype}
					options={ADMIN_TASK_KEYS.map(
						(key): SelectOption => ({
							value: key,
							label: t(`event.add.subtypes.adminType.${key}`),
						}),
					)}
					placeholder={t("event.add.fields.subtype.placeholder")}
					onChange={(v) => setState((s) => ({ ...s, subtype: v || null }))}
				/>
			</FormField>

			<DatePicker
				id={dateId}
				label={t("event.add.fields.date.label")}
				required
				selected={state.date ?? undefined}
				onSelect={(d) => setState((s) => ({ ...s, date: d ?? null }))}
			/>

			<TimePicker
				id={timeId}
				label={t("event.add.fields.time.label")}
				required
				value={state.time ?? undefined}
				onChange={(v) => setState((s) => ({ ...s, time: v }))}
			/>

			<ContactPicker
				id={contactId}
				label={t("event.add.fields.contact.label")}
				required
				value={state.contactId}
				onSelect={(providerUserId) =>
					setState((s) => ({ ...s, contactId: providerUserId }))
				}
			/>

			<FormField id={notesId} label={t("event.add.fields.notes.label")}>
				<textarea
					id={notesId}
					className={styles.textarea}
					value={state.notes}
					placeholder={t("event.add.fields.notes.placeholder")}
					onChange={(e) => setState((s) => ({ ...s, notes: e.target.value }))}
				/>
			</FormField>
		</EventFormShell>
	);
}
